import React, { useEffect, useMemo, useState } from "react";

//MUI
import { Box, CircularProgress, Grid, Typography } from "@mui/material";

//Component
import FunnelChart from "./charts/FunnelChart";
import WaterfallChart from "./charts/waterfallChart";

//Utils
import { showThousandSuperator } from "../utility/utils";
import { DataGrid, GridColDef } from "@mui/x-data-grid";
import BidOverviewColumns from "./BidOverviewDataTable";
import { useAppDispatch, useAppSelector } from "../redux/hooks/redux-hooks";
import { DashboardDetailsModel } from "../models/DashboardDetailsModel";
import { appConfig, projectBidStatusObj } from "../utility/config";
import { WaterfalldetailsModel } from "../models/WaterfallDetailsModel";
// import CustomDatePicker from "./DatePicker";
import { fetchWaterFallDetails } from "../redux/actions/WaterFallDetailsAction";

type Props = {
  dashboardFilteredResponse: DashboardDetailsModel[];
  filters: any;
  fromDate: Date;
  endDate: Date;
};

const BidOverview = ({
  dashboardFilteredResponse,
  filters,
  fromDate,
  endDate,
}: Props) => {
  const [waterfallFilteredResponse, setWaterfallFilteredResponse] = useState<
    WaterfalldetailsModel[]
  >([]);
  const dispatch = useAppDispatch();
  const loggedInUser = useAppSelector((state) => state.user.loggedIn_User);

  const [funnelGridData, setFunnelGridData] = useState<any>({});
  const [funnelDisplayData, setFunnelDisplayData] = useState<any>({});
  const [waterfallGridData, setWaterfallGridData] = useState<any>({});

  const isDashboardDetailsFetching = useAppSelector(
    (state) => state.dashboardDetails.isFetching
  );

  // To get the price value of card
  const getCardPrice = (price: number, totalPrice: number) => {
    if (!price) {
      return "0 | 0";
    }

    const percentage = Math.round((price / totalPrice) * 100);
    return `${showThousandSuperator(price)} Cr | ${
      Number.isInteger(percentage) ? percentage : percentage.toFixed(2)
    }%`;
  };

  // To get the count value
  const getCardCount = (count: number, totalCount: number) => {
    if (!count) {
      return "0 | 0";
    }

    const percentage = Math.round((count / totalCount) * 100);
    return `${count} | ${
      Number.isInteger(percentage) ? percentage : percentage.toFixed(2)
    }%`;
  };

  const columns = useMemo(() => {
    const projectColumns: GridColDef[] = [...BidOverviewColumns];
    projectColumns.forEach((each) => {
      if (
        each.headerName === "Go-NoGo Status" ||
        each.headerName === "Win/Loss Status"
      ) {
        each.headerName = "Go-NoGo Status";
      }
    });
    return projectColumns;
  }, [BidOverviewColumns]);

  const BidOverviewrows = useMemo(() => {
    const rows: DashboardDetailsModel[] = [];
    const dbFromDate = new Date(fromDate);
    const dbEndDate = new Date(endDate);
    dashboardFilteredResponse.forEach((eachitem) => {
      const dbBidSubmittedDate = new Date(eachitem?.bidSubmittedStatusDate);
      const projectStatus = eachitem.projectStatus.toLowerCase();
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        projectStatus !== projectBidStatusObj.rejected &&
        projectStatus !== projectBidStatusObj.loss &&
        projectStatus !== projectBidStatusObj.win &&
        projectStatus !== projectBidStatusObj.bidSubmitted &&
        projectStatus !== projectBidStatusObj.dropped
      ) {
        if (projectStatus === projectBidStatusObj.drafted) {
          rows.push({ ...eachitem, projectStatus: "Completed" });
        } else {
          rows.push(eachitem);
        }
      }
    });
    return rows;
  }, [dashboardFilteredResponse, fromDate, endDate]);

  useEffect(() => {
    const counts = {
      bidUniverse: {
        count: 0,
        value: 0,
      },
      totalGoNoGo: {
        count: 0,
        value: 0,
      },
      bidPipeline: {
        count: 0,
        value: 0,
      },
      totalGoNoGoApproved: {
        count: 0,
        value: 0,
      },
      droppedAfterGoNoGo: {
        count: 0,
        value: 0,
      },
      submitted: {
        count: 0,
        value: 0,
      },
      goNoGoPendingApproval: {
        count: 0,
        value: 0,
      },
      bidSubmitted: {
        count: 0,
        value: 0,
      },
      win: {
        count: 0,
        value: 0,
      },
      loss: {
        count: 0,
        value: 0,
      },
    };
    dashboardFilteredResponse.forEach((each) => {
      const projectStatus = each.projectStatus.toLowerCase();
      const dbFromDate = new Date(fromDate);
      const dbEndDate = new Date(endDate);
      const dbBidSubmittedDate = new Date(each?.bidSubmittedStatusDate);

      if (each.projectID) {
        counts.bidUniverse.count++;
        counts.bidUniverse.value += each.estimatedValue;
      }

      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        (projectStatus === projectBidStatusObj.approved ||
          projectStatus === projectBidStatusObj.drafted ||
          projectStatus === projectBidStatusObj.savedAsDraft ||
          projectStatus === projectBidStatusObj.submitted)
      ) {
        counts.bidPipeline.count++;
        counts.bidPipeline.value += each.estimatedValue;
      }
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        (projectStatus === projectBidStatusObj.approved ||
          projectStatus === projectBidStatusObj.bidSubmitted ||
          projectStatus === projectBidStatusObj.rejected ||
          projectStatus === projectBidStatusObj.submitted ||
          projectStatus === projectBidStatusObj.loss ||
          projectStatus === projectBidStatusObj.win)
      ) {
        counts.submitted.count++;
        counts.submitted.value += each.estimatedValue;
      }
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        (projectStatus === projectBidStatusObj.approved ||
          projectStatus === projectBidStatusObj.submitted ||
          projectStatus === projectBidStatusObj.win ||
          projectStatus === projectBidStatusObj.loss ||
          projectStatus === projectBidStatusObj.bidSubmitted)
      ) {
        counts.totalGoNoGo.count++;
        counts.totalGoNoGo.value += each.estimatedValue;
      }
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        (projectStatus === projectBidStatusObj.approved ||
          projectStatus === projectBidStatusObj.win ||
          projectStatus === projectBidStatusObj.loss ||
          projectStatus === projectBidStatusObj.bidSubmitted)
      ) {
        counts.totalGoNoGoApproved.count++;
        counts.totalGoNoGoApproved.value += each.estimatedValue;
      }
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        projectStatus === projectBidStatusObj.submitted
      ) {
        counts.goNoGoPendingApproval.count++;
        counts.goNoGoPendingApproval.value += each.estimatedValue;
      }
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        projectStatus === projectBidStatusObj.droppedAfterGoNoGo
      ) {
        counts.droppedAfterGoNoGo.count++;
        counts.droppedAfterGoNoGo.value += each.estimatedValue;
      }
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        (projectStatus === projectBidStatusObj.win ||
          projectStatus === projectBidStatusObj.loss ||
          projectStatus === projectBidStatusObj.bidSubmitted)
      ) {
        counts.bidSubmitted.count++;
        counts.bidSubmitted.value += each.estimatedValue;
      }
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        projectStatus === projectBidStatusObj.win
      ) {
        counts.win.count++;
        counts.win.value += each.estimatedValue;
      }
      if (
        dbFromDate <= dbBidSubmittedDate &&
        dbBidSubmittedDate <= dbEndDate &&
        projectStatus === projectBidStatusObj.loss
      ) {
        counts.loss.count++;
        counts.loss.value += each.estimatedValue;
      }
    });

    setFunnelGridData({
      bidPipeline: counts.bidPipeline,
      totalGoNoGo: counts.totalGoNoGo,
      totalGoNoGoApproved: counts.totalGoNoGoApproved,
      bidSubmitted: counts.bidSubmitted,
      win: counts.win,
      submitted: counts.submitted,
      bidUniverse: counts.bidUniverse,
    });
    setFunnelDisplayData({
      bidPipeline: counts.bidPipeline,
      totalGoNoGo: counts.totalGoNoGo,
      droppedAfterGoNoGo: counts.droppedAfterGoNoGo,
      goNoGoPendingApproval: counts.goNoGoPendingApproval,
      loss: counts.loss,
      bidSubmitted: counts.bidSubmitted,
    });
  }, [dashboardFilteredResponse, fromDate, endDate]);

  const funnelChartData = useMemo(() => {
    return [
      {
        category: "Total Go-NoGo",
        count: funnelGridData.totalGoNoGo?.count || 0,

        percentageTxt:
          getCardCount(
            funnelGridData.totalGoNoGo?.count || 0,
            funnelGridData.totalGoNoGo?.count || 0
          ) +
          ", " +
          `INR ${getCardPrice(
            funnelGridData.totalGoNoGo?.value || 0,
            funnelGridData.totalGoNoGo?.value || 0
          )}`,
      },
      {
        category: "Go-NoGo Approved",
        count: funnelGridData.totalGoNoGoApproved?.count || 0,
        percentageTxt:
          getCardCount(
            funnelGridData.totalGoNoGoApproved?.count || 0,
            funnelGridData.submitted?.count || 0
          ) +
          ", " +
          `INR ${getCardPrice(
            funnelGridData.totalGoNoGoApproved?.value || 0,
            funnelGridData.submitted?.value || 0
          )}`,
      },

      {
        category: "Bid Submitted",
        count: funnelGridData.bidSubmitted?.count || 0,
        percentageTxt:
          getCardCount(
            funnelGridData.bidSubmitted?.count || 0,
            funnelGridData.totalGoNoGoApproved?.count || 0
          ) +
          ", " +
          `INR ${getCardPrice(
            funnelGridData.bidSubmitted?.value || 0,
            funnelGridData.totalGoNoGoApproved?.value || 0
          )}`,
      },
      {
        category: "Won",
        count: funnelGridData.win?.count || 0,
        percentageTxt:
          getCardCount(
            funnelGridData.win?.count || 0,
            funnelGridData.bidSubmitted?.count || 0
          ) +
          ", " +
          `INR ${getCardPrice(
            funnelGridData.win?.value || 0,
            funnelGridData.bidSubmitted?.value || 0
          )}`,
      },
    ];
  }, [funnelGridData]);

  useEffect(() => {
    if (loggedInUser.userId) {
      dispatch(fetchWaterFallDetails(loggedInUser.userId));
    }
  }, [loggedInUser, dispatch]);

  const isWaterfallDetailsFetching = useAppSelector(
    (state) => state.waterfallDetails.isFetching
  );
  const warterfallDetails = useAppSelector(
    (state) => state.waterfallDetails.waterfall_details
  );

  useEffect(() => {
    let result = warterfallDetails;

    if (filters.bdHead !== "all") {
      result = result.filter((rec) => rec.bdHead === filters.bdHead);
    }

    if (filters.bdPoc !== "all") {
      result = result.filter((rec) => rec.bdpoc === filters.bdPoc);
    }

    if (filters.sbgHead !== "all") {
      result = result.filter((rec) => rec.sbg === filters.sbgHead);
    }

    if (filters.sbuHead !== "all") {
      result = result.filter((rec) => rec.sbu === filters.sbuHead);
    }

    setWaterfallFilteredResponse(result);
  }, [filters, warterfallDetails]);

  useEffect(() => {
    const counts = {
      startingValuePipeline: {
        count: 0,
        value: 0,
      },
      added: {
        count: 0,
        value: 0,
      },
      dropped: {
        count: 0,
        value: 0,
      },
      goNoGoRejected: {
        count: 0,
        value: 0,
      },
      bidSubmitted: {
        count: 0,
        value: 0,
      },
      closingValuePipeline: {
        count: 0,
        value: 0,
      },
    };

    waterfallFilteredResponse.forEach((each) => {
      const projectStatus = each.projectStatus.toLowerCase();
      const createdDate = new Date(each.createdDate);
      const rejectedDate = each.rejectedDate && new Date(each.rejectedDate);
      const bidDroppedDate =
        each.bidDroppedDate && new Date(each.bidDroppedDate);
      const droppedAfterGonogoDate =
        each.droppedAfterGonogoDate && new Date(each.droppedAfterGonogoDate);
      const cancelledDate = each.cancelledDate && new Date(each.cancelledDate);
      const bidSubmittedDate =
        each.bidSubmittedDate && new Date(each.bidSubmittedDate);

      const today = new Date();
      let fyStartDate;
      const month = today.getMonth() + 1;
      if (month === 1 || month === 2 || month === 3) {
        fyStartDate = new Date(`${today.getFullYear() - 1}-04-01`);
      } else {
        fyStartDate = new Date(`${today.getFullYear()}-04-01`);
      }

      if (
        createdDate < fyStartDate &&
        ((projectStatus === projectBidStatusObj.rejected &&
          rejectedDate &&
          rejectedDate >= fyStartDate) ||
          (projectStatus === projectBidStatusObj.dropped &&
            bidDroppedDate &&
            bidDroppedDate >= fyStartDate) ||
          (projectStatus === projectBidStatusObj.droppedAfterGoNoGo &&
            droppedAfterGonogoDate &&
            droppedAfterGonogoDate >= fyStartDate) ||
          (projectStatus === projectBidStatusObj.cancelled &&
            cancelledDate &&
            cancelledDate >= fyStartDate) ||
          ((projectStatus === projectBidStatusObj.bidSubmitted ||
            projectStatus === projectBidStatusObj.win ||
            projectStatus === projectBidStatusObj.loss) &&
            bidSubmittedDate &&
            bidSubmittedDate >= fyStartDate) ||
          projectStatus === projectBidStatusObj.drafted ||
          projectStatus === projectBidStatusObj.savedAsDraft ||
          projectStatus === projectBidStatusObj.submitted ||
          projectStatus === projectBidStatusObj.approved)
      ) {
        counts.startingValuePipeline.count++;
        counts.startingValuePipeline.value += each.estimatedValue;
      }
      if (fyStartDate <= createdDate && createdDate <= today) {
        counts.added.count++;
        counts.added.value += each.estimatedValue;
      }
      if (
        ((projectStatus === projectBidStatusObj.dropped ||
          projectStatus === projectBidStatusObj.droppedAfterGoNoGo) &&
          fyStartDate <= createdDate &&
          createdDate <= today) ||
        (createdDate < fyStartDate &&
          ((projectStatus === projectBidStatusObj.dropped &&
            bidDroppedDate &&
            bidDroppedDate >= fyStartDate) ||
            (projectStatus === projectBidStatusObj.droppedAfterGoNoGo &&
              droppedAfterGonogoDate &&
              droppedAfterGonogoDate >= fyStartDate)))
      ) {
        counts.dropped.count++;
        counts.dropped.value += each.estimatedValue;
      }
      if (
        (projectStatus === projectBidStatusObj.rejected &&
          fyStartDate <= createdDate &&
          createdDate <= today) ||
        (createdDate < fyStartDate &&
          projectStatus === projectBidStatusObj.rejected &&
          rejectedDate &&
          rejectedDate >= fyStartDate)
      ) {
        counts.goNoGoRejected.count++;
        counts.goNoGoRejected.value += each.estimatedValue;
      }
      if (
        ((projectStatus === projectBidStatusObj.bidSubmitted ||
          projectStatus === projectBidStatusObj.win ||
          projectStatus === projectBidStatusObj.loss) &&
          fyStartDate <= createdDate &&
          createdDate <= today) ||
        (createdDate < fyStartDate &&
          (projectStatus === projectBidStatusObj.bidSubmitted ||
            projectStatus === projectBidStatusObj.win ||
            projectStatus === projectBidStatusObj.loss) &&
          bidSubmittedDate &&
          bidSubmittedDate >= fyStartDate)
      ) {
        counts.bidSubmitted.count++;
        counts.bidSubmitted.value += each.estimatedValue;
      }
    });
    counts.closingValuePipeline.count =
      counts.startingValuePipeline.count +
      counts.added.count -
      counts.dropped.count -
      counts.goNoGoRejected.count -
      counts.bidSubmitted.count;
    counts.closingValuePipeline.value =
      counts.startingValuePipeline.value +
      counts.added.value -
      counts.dropped.value -
      counts.goNoGoRejected.value -
      counts.bidSubmitted.value;

    setWaterfallGridData({
      startingValuePipeline: counts.startingValuePipeline,
      added: counts.added,
      dropped: counts.dropped,
      goNoGoRejected: counts.goNoGoRejected,
      bidSubmitted: counts.bidSubmitted,
      closingValuePipeline: counts.closingValuePipeline,
    });
  }, [waterfallFilteredResponse]);

  const waterfallChartData = useMemo(() => {
    return [
      {
        category: "Opening",
        value: waterfallGridData.startingValuePipeline?.value,
        open: 0,
        stepValue: waterfallGridData.startingValuePipeline?.value - 0,
        displayValue: `#: ${
          waterfallGridData.startingValuePipeline?.count
        }, INR ${waterfallGridData.startingValuePipeline?.value.toLocaleString(
          "en-US"
        )} Cr`,
      },
      {
        category: "Added",
        value:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value,
        open: waterfallGridData.startingValuePipeline?.value,
        stepValue:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value,
        displayValue: `#: ${
          waterfallGridData.added?.count
        }, INR ${waterfallGridData.added?.value.toLocaleString("en-US")} Cr`,
      },
      {
        category: "Dropped",
        value:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value,
        open:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value,
        stepValue:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value,
        displayValue: `#: ${
          waterfallGridData.dropped?.count
        }, INR ${waterfallGridData.dropped?.value.toLocaleString("en-US")} Cr`,
      },
      {
        category: "GoNoGo Rejected",
        value:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value -
          waterfallGridData.goNoGoRejected?.value,
        open:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value,
        stepValue:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value -
          waterfallGridData.goNoGoRejected?.value,
        displayValue: `#: ${
          waterfallGridData.goNoGoRejected?.count
        }, INR ${waterfallGridData.goNoGoRejected?.value.toLocaleString(
          "en-US"
        )} Cr`,
      },
      {
        category: "Bid Submitted",
        value:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value -
          waterfallGridData.goNoGoRejected?.value -
          waterfallGridData.bidSubmitted?.value,
        open:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value -
          waterfallGridData.goNoGoRejected?.value,
        stepValue:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value -
          waterfallGridData.goNoGoRejected?.value -
          waterfallGridData.bidSubmitted?.value,
        displayValue: `#: ${
          waterfallGridData.bidSubmitted?.count
        }, INR ${waterfallGridData.bidSubmitted?.value.toLocaleString(
          "en-US"
        )} Cr`,
      },
      {
        category: "Closing",
        value:
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value -
          waterfallGridData.goNoGoRejected?.value -
          waterfallGridData.bidSubmitted?.value,
        open: 0,
        displayValue: `#: ${
          waterfallGridData.startingValuePipeline?.count +
          waterfallGridData.added?.count -
          waterfallGridData.dropped?.count -
          waterfallGridData.goNoGoRejected?.count -
          waterfallGridData.bidSubmitted?.count
        } ,INR ${(
          waterfallGridData.startingValuePipeline?.value +
          waterfallGridData.added?.value -
          waterfallGridData.dropped?.value -
          waterfallGridData.goNoGoRejected?.value -
          waterfallGridData.bidSubmitted?.value
        ).toLocaleString("en-US")} Cr`,
      },
    ];
  }, [waterfallGridData]);

  return (
    <>
      <Grid
        container
        height={`calc(100vh - ${
          appConfig.headerHeight + appConfig.dashboardStackHeight
        }px) `}
        sx={{ overflowY: "auto" }}
      >
        <Grid item xs={12} md={6}>
          {isDashboardDetailsFetching ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress color="primary" className="loader-spinner" />
            </div>
          ) : (
            <>
              <Box padding={1}>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  Bid Pipeline :&nbsp;
                  {getCardCount(
                    funnelDisplayData.bidPipeline?.count || 0,
                    funnelDisplayData.bidPipeline?.count || 0
                  ) +
                    ", " +
                    `INR ${getCardPrice(
                      funnelDisplayData.bidPipeline?.value || 0,
                      funnelDisplayData.bidPipeline?.value || 0
                    )}`}
                </Typography>
              </Box>
              <Grid item className="chart-container">
                <FunnelChart data={funnelChartData} />
              </Grid>
              <Box display="flex" flexDirection="column" padding={1}>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  Go-NoGo Pending For Approval :&nbsp;
                  {getCardCount(
                    funnelDisplayData.goNoGoPendingApproval?.count || 0,
                    funnelDisplayData.totalGoNoGo?.count || 0
                  ) +
                    ", " +
                    `INR ${getCardPrice(
                      funnelDisplayData.goNoGoPendingApproval?.value || 0,
                      funnelDisplayData.totalGoNoGo?.value || 0
                    )}`}
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  Dropped After GoNoGo :&nbsp;
                  {getCardCount(
                    funnelDisplayData.droppedAfterGoNoGo?.count || 0,
                    funnelDisplayData.totalGoNoGo?.count || 0
                  ) +
                    ", " +
                    `INR ${getCardPrice(
                      funnelDisplayData.droppedAfterGoNoGo?.value || 0,
                      funnelDisplayData.totalGoNoGo?.value || 0
                    )}`}
                </Typography>
                <Typography sx={{ fontWeight: 600, fontSize: 13 }}>
                  Lost :&nbsp;
                  {getCardCount(
                    funnelDisplayData.loss?.count || 0,
                    funnelDisplayData.bidSubmitted?.count || 0
                  ) +
                    ", " +
                    `INR ${getCardPrice(
                      funnelDisplayData.loss?.value || 0,
                      funnelDisplayData.bidSubmitted?.value || 0
                    )}`}
                </Typography>
              </Box>
            </>
          )}
        </Grid>
        <Grid item xs={12} md={6}>
          {isWaterfallDetailsFetching ? (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "100%",
              }}
            >
              <CircularProgress color="primary" className="loader-spinner" />
            </div>
          ) : (
            <>
              <h6
                style={{
                  textAlign: "center",
                  paddingTop: "8px",
                  fontWeight: 600,
                }}
              >
                FY-YTD Pipeline Status (Count, Value)
              </h6>
              <Grid item className="chart-container">
                <WaterfallChart data={waterfallChartData} />
              </Grid>
            </>
          )}
        </Grid>
        <Grid item xs={12} md={12}>
          <DataGrid
            columns={columns}
            rows={BidOverviewrows}
            getRowId={(rows) => rows.projectID}
            style={{ height: 350, width: "100%", marginTop: "5px" }}
            sx={{
              ".MuiDataGrid-columnHeaders": {
                backgroundColor: "#348fea",
              },
            }}
            disableRowSelectionOnClick
          />
        </Grid>
      </Grid>
    </>
  );
};

export default BidOverview;
